// src/components/Wishlist.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import LoadingState from "./LoadingState";
import { colors, type, radii, cardStyle, buttonPrimary, buttonSecondary, primaryHover } from "../styles/designTokens";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({ publicKey: currentKey });

const Wishlist = () => {
  const navigate = useNavigate();
  useNoIndex();

  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [ownItems, setOwnItems] = useState([]);
  const [sharedItems, setSharedItems] = useState([]);
  const [circles, setCircles] = useState([]);
  const [connections, setConnections] = useState([]);
  const [shareUrl, setShareUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newIsPublic, setNewIsPublic] = useState(false);
  const [sharingItemId, setSharingItemId] = useState(null);
  const [shareTargetKey, setShareTargetKey] = useState("");

  useEffect(() => {
    const validateAndLoad = async () => {
      const token = localStorage.getItem("memberstackToken");
      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      let currentMember = null;
      try {
        currentMember = await memberstack.getCurrentMember();
      } catch (err) {
        console.error("Wishlist › getCurrentMember error:", err);
      }

      if (!currentMember || !currentMember.data) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      const hasValidStatus = (currentMember.data.planConnections || []).some((plan) => {
        const status = plan.status?.toLowerCase();
        const type = plan.type?.toLowerCase();
        return type === "subscription" && ["active", "trialing", "cancelled", "canceled"].includes(status);
      });

      setIsVerifyingAuth(false);

      if (!hasValidStatus) {
        navigate("/checkout");
        return;
      }

      await loadData(token);
      setLoading(false);
    };

    validateAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadData = async (token) => {
    try {
      const [wishlistData, circlesData, connectionsData] = await Promise.all([
        standardUserApi.getWishlist(token),
        standardUserApi.getCircles(token),
        standardUserApi.getConnections(token),
      ]);
      setOwnItems(wishlistData.own);
      setSharedItems(wishlistData.shared);
      setCircles(circlesData);
      setConnections(connectionsData.filter((c) => c.status === "accepted"));

      if (wishlistData.own.some((i) => i.isPublic)) {
        const tokenData = await standardUserApi.getWishlistShareToken(token);
        setShareUrl(tokenData.url);
      }
    } catch (err) {
      console.error("Wishlist › load error:", err);
      setErrorMessage("Couldn't load your wishlist right now. Please try again shortly.");
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.createWishlistItem(token, {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        isPublic: newIsPublic,
      });
      setNewTitle("");
      setNewDescription("");
      setNewIsPublic(false);
      await loadData(token);
    } catch (err) {
      console.error("Wishlist › create error:", err);
      setErrorMessage("Couldn't save that idea right now. Please try again shortly.");
    }
  };

  const handleDelete = async (itemId) => {
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.deleteWishlistItem(token, itemId);
      await loadData(token);
    } catch (err) {
      console.error("Wishlist › delete error:", err);
      setErrorMessage("Couldn't remove that right now. Please try again shortly.");
    }
  };

  const handleShare = async (itemId) => {
    if (!shareTargetKey) return;
    const [type, id] = shareTargetKey.split(":");
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.shareWishlistItem(token, itemId, {
        connectedUserId: type === "connection" ? Number(id) : undefined,
        circleId: type === "circle" ? Number(id) : undefined,
      });
      setSharingItemId(null);
      setShareTargetKey("");
      setSuccessMessage("Shared.");
    } catch (err) {
      console.error("Wishlist › share error:", err);
      setErrorMessage("Couldn't share that right now. Please try again shortly.");
    }
  };

  const handleTurnIntoHangout = (item) => {
    navigate("/circles", { state: { prefillTitle: item.title, prefillDescription: item.description } });
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url);
    setSuccessMessage("Link copied.");
  };

  if (isVerifyingAuth || loading) {
    return <LoadingState />;
  }

  if (showLoginModal) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <p style={{ color: colors.body, marginBottom: "16px" }}>Please log in to view your wishlist.</p>
        <Link to="/login" style={{ color: colors.primary, fontWeight: "700" }}>Log in</Link>
      </div>
    );
  }

  return (
    <>
      <div style={{ background: colors.gradientTint, borderBottom: `1px solid ${colors.primaryTintBorder}` }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", padding: "48px 20px 36px", textAlign: "center" }}>
          <p style={type.eyebrow}>Ideas worth acting on</p>
          <h1 style={{ ...type.h1, marginTop: "10px", marginBottom: "10px" }}>Wishlist</h1>
          <p style={{ ...type.body, maxWidth: "440px", margin: "0 auto 24px" }}>
            Save social ideas, share them with your circle, and turn any of them into a real hangout
            the moment someone else is in.
          </p>
          {shareUrl && (
            <button onClick={() => copyLink(shareUrl)} style={buttonSecondary}>
              🔗 Copy your public wishlist link
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "36px 20px 64px" }}>
        {errorMessage && <p style={{ color: colors.danger, textAlign: "center", marginBottom: "16px", fontSize: "0.9rem" }}>{errorMessage}</p>}
        {successMessage && <p style={{ color: colors.success, textAlign: "center", marginBottom: "16px", fontSize: "0.9rem" }}>{successMessage}</p>}

        <h2 style={{ ...type.h2, marginBottom: "12px" }}>Your Ideas</h2>
        {ownItems.length === 0 && (
          <div style={{ ...cardStyle, textAlign: "center", padding: "28px 24px", marginBottom: "16px" }}>
            <p style={{ color: colors.muted, fontSize: "0.9rem" }}>No ideas saved yet — add your first one below.</p>
          </div>
        )}
        {ownItems.map((item) => (
          <div key={item.id} style={{ ...cardStyle, padding: "16px 20px", marginBottom: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ color: colors.ink, fontWeight: "700", fontSize: "0.98rem" }}>{item.title}</div>
                {item.description && <div style={{ color: colors.muted, fontSize: "0.88rem", marginTop: "2px" }}>{item.description}</div>}
                <div style={{ color: colors.faint, fontSize: "0.8rem", marginTop: "6px" }}>
                  {item.isPublic ? "🌐 Public" : "🔒 Private"} · {item.interestCount} interested
                </div>
              </div>
              <button onClick={() => handleDelete(item.id)} style={{ background: "none", border: "none", color: colors.faint, cursor: "pointer", fontSize: "0.82rem" }}>
                Remove
              </button>
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
              <button
                onClick={() => handleTurnIntoHangout(item)}
                style={{ ...buttonPrimary, padding: "8px 16px", fontSize: "0.82rem", boxShadow: "none" }}
                {...primaryHover}
              >
                Turn into a hangout
              </button>
              {sharingItemId === item.id ? (
                <>
                  <select
                    value={shareTargetKey}
                    onChange={(e) => setShareTargetKey(e.target.value)}
                    style={{ padding: "8px 10px", borderRadius: radii.sm, border: `1.5px solid ${colors.border}`, fontSize: "0.82rem" }}
                  >
                    <option value="">Share with...</option>
                    {connections.map((c) => (
                      <option key={`connection:${c.id}`} value={`connection:${c.id}`}>
                        {c.otherUser.firstName} {c.otherUser.lastName}
                      </option>
                    ))}
                    {circles.map((c) => (
                      <option key={`circle:${c.id}`} value={`circle:${c.id}`}>{c.name} (circle)</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleShare(item.id)}
                    disabled={!shareTargetKey}
                    style={{ ...buttonSecondary, padding: "8px 16px", fontSize: "0.82rem" }}
                  >
                    Confirm
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setSharingItemId(item.id)}
                  style={{ backgroundColor: "transparent", color: colors.muted, border: `1.5px solid ${colors.border}`, borderRadius: radii.pill, padding: "8px 16px", fontWeight: "600", cursor: "pointer", fontSize: "0.82rem" }}
                >
                  Share
                </button>
              )}
            </div>
          </div>
        ))}

        <form onSubmit={handleAddItem} style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: "10px", marginTop: "20px", marginBottom: "32px" }}>
          <input
            type="text"
            placeholder="An idea (e.g. 'Try that new ramen place')"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            style={{ padding: "10px 14px", borderRadius: radii.sm, border: `1.5px solid ${colors.border}`, fontSize: "0.9rem" }}
          />
          <input
            type="text"
            placeholder="Details (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            style={{ padding: "10px 14px", borderRadius: radii.sm, border: `1.5px solid ${colors.border}`, fontSize: "0.9rem" }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: "8px", color: colors.body, fontSize: "0.88rem" }}>
            <input type="checkbox" checked={newIsPublic} onChange={(e) => setNewIsPublic(e.target.checked)} />
            Make this public (visible on your shareable wishlist link, even to people without an OkGather account)
          </label>
          <button
            type="submit"
            style={{ ...buttonPrimary, padding: "10px 22px", fontSize: "0.9rem", alignSelf: "flex-start" }}
            {...primaryHover}
          >
            Add idea
          </button>
        </form>

        {sharedItems.length > 0 && (
          <>
            <h2 style={{ ...type.h2, marginBottom: "12px" }}>Shared With You</h2>
            {sharedItems.map((item) => (
              <div key={item.id} style={{ ...cardStyle, padding: "16px 20px", marginBottom: "10px" }}>
                <div style={{ color: colors.ink, fontWeight: "700", fontSize: "0.98rem" }}>{item.title}</div>
                {item.description && <div style={{ color: colors.muted, fontSize: "0.88rem", marginTop: "2px" }}>{item.description}</div>}
                <div style={{ color: colors.faint, fontSize: "0.8rem", marginTop: "6px" }}>From {item.ownerFirstName}</div>
                <button
                  onClick={() => handleTurnIntoHangout(item)}
                  style={{ ...buttonPrimary, marginTop: "10px", padding: "8px 16px", fontSize: "0.82rem", boxShadow: "none" }}
                  {...primaryHover}
                >
                  Turn into a hangout
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
};

export default Wishlist;
